import React, { useEffect, useRef, useState } from 'react';
import { motion, MotionValue, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import config from '@/config/config';
import blueprint from './assets/ic3-blueprint.webp';
import james from './assets/james.webp';
import joel from './assets/joel.webp';
import josh from './assets/josh.webp';
import './conference.css';

const eventId = '3b9d2344-6f05-4815-8b12-2f29251b6d99';

// Production uses the current origin so the iframe always passes X-Frame-Options: SAMEORIGIN.
const webAppUrl = process.env.NODE_ENV === 'production' ? `${window.location.origin}/webui` : config.webAppUrl;

const SHEETS = [
  { id: 'entrance', label: 'Entrance' },
  { id: 'why', label: 'Why we gather' },
  { id: 'days', label: 'Three days' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'register', label: 'Register' },
];

// Placeholder copy from here down, to be replaced with the final wording.
const STATEMENT = 'Every church is built on something. For three days, pastors, leaders and teams from across the Philippines and beyond will come to Cebu to look closely at what they are building, and what it stands on.';

const DAYS = [
  { date: 'Thursday, February 4', title: 'The foundation', copy: 'We start underground, with the truths that hold everything else up. Worship, teaching and time to ask what your ministry really rests on.' },
  { date: 'Friday, February 5', title: 'The frame', copy: 'Then we build upward. Practical sessions on leadership, teams and discipleship, the structure that lets a church grow without losing its shape.' },
  { date: 'Saturday, February 6', title: 'The open doors', copy: 'A building is finished when people walk in. We close by looking outward, and we send you home ready to open yours.' },
];

const SPEAKERS = [
  { id: 'james', name: 'Ps. James Aiton', church: 'Favor Church', detail: 'Session details coming soon', image: james },
  { id: 'joel', name: 'Bs. Joel Montes', church: 'CLC Davao', detail: 'Session details coming soon', image: joel },
  { id: 'josh', name: 'Bs. Josh Obeng', church: 'CCC Ghana', detail: 'Session details coming soon', image: josh },
];

// With reduced motion every scroll-linked value rests at its finished state.
const useStoryProgress = (ref: React.RefObject<HTMLElement>, reduceMotion: boolean | null): MotionValue<number> => {
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const smooth = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const finished = useMotionValue(1);
  return reduceMotion ? finished : smooth;
};

const Entrance: React.FC<{ reduceMotion: boolean | null }> = ({ reduceMotion }) => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const scale = useTransform(progress, [0, 0.86], [1, 5]);
  const planOpacity = useTransform(progress, [0.7, 0.92], [1, 0]);
  const stageColor = useTransform(progress, [0.78, 0.98], ['#0c4a90', '#1b67d2']);
  const titleY = useTransform(progress, [0, 0.3], ['0vh', '-16vh']);
  const titleOpacity = useTransform(progress, [0.08, 0.28], [1, 0]);
  const lineOpacity = useTransform(progress, [0.36, 0.46, 0.7, 0.8], [0, 1, 1, 0]);

  return (
    <section ref={ref} id="entrance" className="cx-entrance">
      <motion.div className="cx-entrance__stage" style={reduceMotion ? undefined : { backgroundColor: stageColor }}>
        <motion.img
          src={blueprint}
          alt="Blueprint elevation of the IC3 Convention Center in Cebu"
          className="cx-entrance__plan"
          style={reduceMotion ? undefined : { scale, opacity: planOpacity }}
        />
        <motion.header className="cx-entrance__title" style={reduceMotion ? undefined : { y: titleY, opacity: titleOpacity }}>
          <h1>
            <span className="cx-blackletter">Crossroads Conference</span>
            <span className="cx-entrance__year">2027</span>
          </h1>
          <p>February 4 to 6, IC3 Convention Center, Cebu</p>
        </motion.header>
      </motion.div>
    </section>
  );
};

const Word: React.FC<{ word: string; index: number; total: number; progress: MotionValue<number> }> = ({ word, index, total, progress }) => {
  const start = (index / total) * 0.85;
  const opacity = useTransform(progress, [start, start + 0.12], [0.16, 1]);
  return <motion.span style={{ opacity }}>{word} </motion.span>;
};

const Statement: React.FC<{ reduceMotion: boolean | null }> = ({ reduceMotion }) => {
  const ref = useRef<HTMLElement>(null);
  const progress = useStoryProgress(ref, reduceMotion);
  const words = STATEMENT.split(' ');

  return (
    <section ref={ref} id="why" className="cx-statement">
      <div className="cx-statement__stage">
        <h2 className="cx-sr-only">Why we gather</h2>
        <p>{words.map((word, i) => <Word key={i} word={word} index={i} total={words.length} progress={progress} />)}</p>
      </div>
    </section>
  );
};

// The building's cross-section, drawn in the same order the days run: bottom to top.
const SectionDrawing: React.FC<{ progress: MotionValue<number> }> = ({ progress }) => {
  const foundation = useTransform(progress, [0.02, 0.3], [0, 1]);
  const frame = useTransform(progress, [0.34, 0.63], [0, 1]);
  const roof = useTransform(progress, [0.67, 0.96], [0, 1]);
  const columns = [90, 190, 290, 390, 490];

  return (
    <svg viewBox="0 0 580 520" className="cx-drawing" aria-hidden="true">
      <path d="M0 400H580" className="cx-drawing__ground" />
      <g>
        <motion.path style={{ pathLength: foundation }} d="M60 400V450H520V400" />
        <motion.path style={{ pathLength: foundation }} d="M60 450L30 490H550L520 450" />
        {columns.map((x) => <motion.path key={x} style={{ pathLength: foundation }} d={`M${x - 16} 490V450H${x + 16}V490`} />)}
      </g>
      <g>
        {columns.map((x) => <motion.path key={x} style={{ pathLength: frame }} d={`M${x} 400V150`} />)}
        <motion.path style={{ pathLength: frame }} d="M60 275H520" />
        <motion.path style={{ pathLength: frame }} d="M60 150H520" />
        <motion.path style={{ pathLength: frame }} d="M90 400L190 275M290 400L190 275M290 400L390 275M490 400L390 275" />
      </g>
      <g>
        <motion.path style={{ pathLength: roof }} d="M40 150L290 40L540 150" />
        <motion.path style={{ pathLength: roof }} d="M190 150L290 40L390 150" />
        <motion.path style={{ pathLength: roof }} d="M262 400V320H318V400M290 320V400" />
      </g>
    </svg>
  );
};

const Day: React.FC<{ day: typeof DAYS[number]; index: number; progress: MotionValue<number> }> = ({ day, index, progress }) => {
  const from = index / DAYS.length;
  const to = (index + 1) / DAYS.length;
  const opacity = useTransform(
    progress,
    index === 0 ? [to - 0.05, to] : index === DAYS.length - 1 ? [from, from + 0.05] : [from, from + 0.05, to - 0.05, to],
    index === 0 ? [1, 0] : index === DAYS.length - 1 ? [0, 1] : [0, 1, 1, 0]
  );
  return (
    <motion.article className="cx-day" style={{ opacity }}>
      <p className="cx-day__date">{day.date}</p>
      <h3>{day.title}</h3>
      <p>{day.copy}</p>
    </motion.article>
  );
};

const Days: React.FC<{ reduceMotion: boolean | null }> = ({ reduceMotion }) => {
  const ref = useRef<HTMLElement>(null);
  const progress = useStoryProgress(ref, reduceMotion);

  return (
    <section ref={ref} id="days" className="cx-days">
      <div className="cx-days__stage">
        <h2 className="cx-sr-only">Three days</h2>
        <SectionDrawing progress={progress} />
        <div className="cx-days__copy">
          {DAYS.map((day, i) => <Day key={day.title} day={day} index={i} progress={progress} />)}
        </div>
      </div>
    </section>
  );
};

const Speakers: React.FC<{ reduceMotion: boolean | null }> = ({ reduceMotion }) => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center center'] });
  const finished = useMotionValue(1);
  const progress = reduceMotion ? finished : scrollYProgress;
  const rise = useTransform(progress, [0, 1], ['18%', '0%']);
  const riseLate = useTransform(progress, [0.2, 1], ['30%', '0%']);

  return (
    <section ref={ref} id="speakers" className="cx-speakers">
      <h2>The people speaking</h2>
      <svg width="0" height="0" className="cx-speakers__filter" aria-hidden="true">
        {/* Maps luminance onto the poster's blue, so the cutouts sit inside the blueprint */}
        <filter id="cx-duotone" colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values="0.3 0.59 0.11 0 0  0.3 0.59 0.11 0 0  0.3 0.59 0.11 0 0  0 0 0 1 0" />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="0.05 0.5 0.92" />
            <feFuncG type="table" tableValues="0.24 0.64 0.95" />
            <feFuncB type="table" tableValues="0.62 0.95 1" />
          </feComponentTransfer>
        </filter>
      </svg>
      <div className="cx-speakers__ground">
        {SPEAKERS.map((speaker) => (
          <motion.figure
            key={speaker.id}
            className={`cx-speaker cx-speaker--${speaker.id}`}
            tabIndex={0}
            style={{ y: speaker.id === 'james' ? riseLate : rise }}
          >
            {/* Hover area follows the silhouette, otherwise the front figures would cover James */}
            <span className="cx-speaker__hit" />
            <img src={speaker.image} alt="" className="cx-speaker__photo" />
            <img src={speaker.image} alt={speaker.name} className="cx-speaker__tint" />
            <figcaption>
              <strong>{speaker.name}</strong>
              <span>{speaker.church}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
};

const Register: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'opening' | 'open'>('idle');
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fullFlowUrl = `${webAppUrl}/event-registration/${eventId}/registration-options`;

  useEffect(() => {
    const onMessage = ({ origin, data }: MessageEvent) => {
      if (origin !== new URL(webAppUrl).origin) return;
      if (data?.type === 'registration:ready') {
        setStatus('open');
        iframeRef.current?.focus();
      }
      if (data?.type === 'registration:close') setStatus('idle');
      if (data?.type === 'registration:complete') {
        setIsRegistered(true);
        setReferenceNumber(data.referenceNumber ?? null);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (status === 'idle') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [status]);

  return (
    <section id="register" className="cx-register">
      {isRegistered ? (
        <div aria-live="polite">
          <h2 className="cx-blackletter">See you in Cebu</h2>
          <p>You're registered. Your confirmation email is on its way.</p>
          {referenceNumber && <p>Your reference number is <strong>{referenceNumber}</strong>.</p>}
          <a href={fullFlowUrl}>View your registration</a>
        </div>
      ) : (
        <div>
          <h2 className="cx-blackletter">Take your place</h2>
          <p>February 4 to 6, 2027 at the IC3 Convention Center, Cebu. Hosted by Crossroads Church.</p>
          {eventId ? (
            <>
              <button type="button" className="cx-button" onClick={() => setStatus('opening')}>Register now</button>
              <a href={fullFlowUrl}>Already a member? Log in to register</a>
            </>
          ) : (
            <button type="button" className="cx-button" disabled>Registration opens soon</button>
          )}
        </div>
      )}

      {status !== 'idle' && (
        <div className="cx-overlay" role={status === 'opening' ? 'status' : undefined}>
          {status === 'opening' && (
            <div className="cx-overlay__loading">
              <p>Opening registration…</p>
              <a href={fullFlowUrl}>Open it in a full page instead</a>
              <button type="button" onClick={() => setStatus('idle')}>Cancel</button>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={`${webAppUrl}/event-registration/${eventId}/embed`}
            title="Conference registration"
            className={status === 'open' ? 'is-open' : undefined}
          />
        </div>
      )}
    </section>
  );
};

// A dimension line down the page edge doubles as progress and navigation.
const SheetRail: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const scaleY = useSpring(scrollYProgress, { stiffness: 200, damping: 40 });
  const [active, setActive] = useState(SHEETS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) setActive(entry.target.id); }),
      { rootMargin: '-50% 0px -50% 0px' }
    );
    SHEETS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="cx-rail" aria-label="Page sections">
      <motion.span className="cx-rail__line" style={{ scaleY }} />
      <ol>
        {SHEETS.map(({ id, label }) => (
          <li key={id}>
            <a href={`#${id}`} aria-current={active === id ? 'true' : undefined}><span>{label}</span></a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

const ConferencePage: React.FC = () => {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Crossroads Conference 2027';
    return () => { document.title = previous; };
  }, []);

  return (
    <main className={`cx${reduceMotion ? ' cx--static' : ''}`}>
      <SheetRail />
      <Entrance reduceMotion={reduceMotion} />
      <Statement reduceMotion={reduceMotion} />
      {/* <Days reduceMotion={reduceMotion} /> */}
      <Speakers reduceMotion={reduceMotion} />
      <Register />
    </main>
  );
};

export default ConferencePage;
